import React, { useState } from 'react';
import { User, Mail, Phone, CheckCircle, ChevronLeft, Stethoscope } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSuccess } from './SuccessContext';

const DrNoumanConsultationSignup = () => {
  const navigate = useNavigate();
  const { displaySuccess } = useSuccess();
  const [state, setState] = useState({
    name: '',
    email: '',
    phone: '',
    currentStep: 1,
    isCompleted: false,
    isLoading: false,
  });

  const handleInputChange = (field, value) => {
    setState(prev => ({ ...prev, [field]: value }));
  };

  const submitForm = async () => {
    setState(prev => ({ ...prev, isLoading: true }));
    
    try {
      const response = await fetch('/api/admin/doctorflow.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: state.name,
          email: state.email,
          phone: state.phone
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Show success notification on home page
        displaySuccess("Successfully submitted! Our team will contact you soon to schedule your consultation with Dr. Nouman.");
        navigate('/');
      } else {
        alert('Error: ' + data.message);
        setState(prev => ({ ...prev, isLoading: false }));
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('An error occurred while submitting the form. Please try again.');
      setState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleContinue = () => {
    if (state.currentStep < 4) {
      setState(prev => ({ ...prev, currentStep: prev.currentStep + 1 }));
    } else {
      submitForm();
    }
  };

  const handleBack = () => {
    if (state.currentStep > 1) {
      setState(prev => ({ ...prev, currentStep: prev.currentStep - 1 }));
    } else {
      navigate('/');
    }
  };

  const canContinue = () => {
    switch (state.currentStep) {
      case 1: return state.name.trim() !== '';
      case 2: return state.email !== '' && /\S+@\S+\.\S+/.test(state.email);
      case 3: return state.phone !== '' && state.phone.length >= 10;
      case 4: return true;
      default: return true;
    }
  };

  const renderStep = () => {
    switch (state.currentStep) {
      case 1: return <NameStep value={state.name} onChange={(value) => handleInputChange('name', value)} />;
      case 2: return <EmailStep value={state.email} onChange={(value) => handleInputChange('email', value)} />;
      case 3: return <PhoneStep value={state.phone} onChange={(value) => handleInputChange('phone', value)} />;
      case 4: return <CompletionStep name={state.name} program="Dr. Nouman One-Time Consultation" />;
      default: return null;
    }
  };

  if (state.isCompleted) {
    return (
      <div className="min-h-screen bg-gray-50 text-gray-800 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center px-4">
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#2B4C7E] mb-4">Thank You!</h2>
            <p className="text-gray-600 mb-6">
              Your consultation request with Dr. Nouman has been submitted successfully. Our team will contact you soon to schedule your session.
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-[#FF6B6B] text-white px-8 py-3 rounded-xl font-semibold hover:bg-[#e55a5a] transition-all duration-300"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">
      <div className="max-w-sm mx-auto min-h-screen flex flex-col">
        <div className="flex items-center justify-between px-4 py-6">
          <button
            onClick={handleBack}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors duration-200"
          >
            <ChevronLeft className="w-6 h-6 text-gray-700" />
          </button>
          
          <div className="text-center">
            <h1 className="text-lg font-bold text-[#2B4C7E]">
              STEP {state.currentStep} OF 4
            </h1>
          </div>
          
          <div className="w-10"></div>
        </div>

        <div className="px-4 mb-8">
          <div className="w-full bg-gray-200 rounded-full h-1">
            <div 
              className="bg-[#2B4C7E] h-1 rounded-full transition-all duration-500"
              style={{ width: `${(state.currentStep / 4) * 100}%` }}
            />
          </div>
        </div>

        <div className="flex-1 px-4 pb-32">
          {renderStep()}
        </div>

        <div className="fixed bottom-0 left-0 right-0 p-4 bg-gray-50 border-t border-gray-200">
          <div className="max-w-sm mx-auto">
            <button
              onClick={handleContinue}
              disabled={!canContinue() || state.isLoading}
              className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 ${
                canContinue() && !state.isLoading
                  ? 'bg-[#FF6B6B] text-white hover:bg-[#e55a5a]'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {state.isLoading ? 'PROCESSING...' : (state.currentStep === 4 ? 'SUBMIT' : 'CONTINUE')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Step Components (similar to other signup forms but with Stethoscope icon)
const NameStep = ({ value, onChange }) => (
  <div className="space-y-8 h-full flex flex-col justify-center">
    <div className="text-center mb-12">
      <div className="flex items-center justify-center mb-4">
        <User className="w-8 h-8 text-[#FF6B6B] mr-3" />
        <h2 className="text-3xl font-bold text-[#2B4C7E]">YOUR NAME</h2>
      </div>
      <p className="text-gray-600 text-lg px-4">What should we call you?</p>
    </div>
    <div className="space-y-6">
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter your full name"
          className="w-full p-6 rounded-2xl bg-white border border-gray-300 text-gray-800 text-lg placeholder-gray-400 focus:outline-none focus:border-[#2B4C7E] focus:ring-2 focus:ring-[#2B4C7E]/20 transition-all duration-300"
        />
        {value.trim() && (
          <CheckCircle className="absolute right-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-green-500" />
        )}
      </div>
    </div>
  </div>
);

const EmailStep = ({ value, onChange }) => {
  const isValid = value !== '' && /\S+@\S+\.\S+/.test(value);
  return (
    <div className="space-y-8 h-full flex flex-col justify-center">
      <div className="text-center mb-12">
        <div className="flex items-center justify-center mb-4">
          <Mail className="w-8 h-8 text-[#FF6B6B] mr-3" />
          <h2 className="text-3xl font-bold text-[#2B4C7E]">EMAIL ADDRESS</h2>
        </div>
        <p className="text-gray-600 text-lg px-4">We'll send consultation details here</p>
      </div>
      <div className="space-y-6">
        <div className="relative">
          <input
            type="email"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="your.email@example.com"
            className="w-full p-6 rounded-2xl bg-white border border-gray-300 text-gray-800 text-lg placeholder-gray-400 focus:outline-none focus:border-[#2B4C7E] focus:ring-2 focus:ring-[#2B4C7E]/20 transition-all duration-300"
          />
          {isValid && (
            <CheckCircle className="absolute right-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-green-500" />
          )}
        </div>
        {value && !isValid && (
          <p className="text-[#FF6B6B] text-sm px-2">Please enter a valid email address</p>
        )}
      </div>
    </div>
  );
};

const PhoneStep = ({ value, onChange }) => {
  const isValid = value.length >= 10;
  return (
    <div className="space-y-8 h-full flex flex-col justify-center">
      <div className="text-center mb-12">
        <div className="flex items-center justify-center mb-4">
          <Phone className="w-8 h-8 text-[#FF6B6B] mr-3" />
          <h2 className="text-3xl font-bold text-[#2B4C7E]">PHONE NUMBER</h2>
        </div>
        <p className="text-gray-600 text-lg px-4">For consultation coordination</p>
      </div>
      <div className="space-y-6">
        <div className="relative">
          <input
            type="tel"
            value={value}
            onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
            placeholder="+1 234 567 8900"
            className="w-full p-6 rounded-2xl bg-white border border-gray-300 text-gray-800 text-lg placeholder-gray-400 focus:outline-none focus:border-[#2B4C7E] focus:ring-2 focus:ring-[#2B4C7E]/20 transition-all duration-300"
          />
          {isValid && (
            <CheckCircle className="absolute right-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-green-500" />
          )}
        </div>
        {value && !isValid && (
          <p className="text-[#FF6B6B] text-sm px-2">Please enter a valid phone number (minimum 10 digits)</p>
        )}
      </div>
    </div>
  );
};

const CompletionStep = ({ name, program }) => (
  <div className="space-y-8 h-full flex flex-col justify-center">
    <div className="text-center mb-12">
      <div className="flex items-center justify-center mb-4">
        <Stethoscope className="w-8 h-8 text-[#FF6B6B] mr-3" />
        <h2 className="text-3xl font-bold text-[#2B4C7E]">ALMOST DONE</h2>
      </div>
      <p className="text-gray-600 text-lg px-4">Hi {name}, you're signing up for {program}!</p>
    </div>
    <div className="bg-white rounded-2xl p-6 border border-gray-200">
      <h3 className="text-xl font-semibold text-[#2B4C7E] mb-4 text-center">What happens next?</h3>
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-6 h-6 bg-[#FF6B6B] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
            <span className="text-white text-sm font-bold">1</span>
          </div>
          <p className="text-gray-700">Our team will contact you within 24 hours to schedule your consultation with Dr. Nouman</p>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-6 h-6 bg-[#FF6B6B] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
            <span className="text-white text-sm font-bold">2</span>
          </div>
          <p className="text-gray-700">We'll send you a confirmation with the consultation details and preparation instructions</p>
        </div>
        <div className="flex items-start gap-3">
          <div className="w-6 h-6 bg-[#FF6B6B] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
            <span className="text-white text-sm font-bold">3</span>
          </div>
          <p className="text-gray-700">You'll receive a personalized health plan after your consultation with Dr. Nouman</p>
        </div>
      </div>
    </div>
  </div>
);

export default DrNoumanConsultationSignup;